import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { get, put } from "../../utils/networkUtils";
import { getGameConfigComponent } from "./game-provider-configs";

const defaultConfig = {
	entries: [2, 5, 10, 20, 50, 100, 250, 500],
	multplier: 2.5,
	winProbability: 0.33,
};

export default function GameProviderGameConfigDialog({ open, gameProviderId, onClose, onSuccessHandler }) {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(false);
	const [gameConfig, setGameConfig] = useState(defaultConfig);
	const [gameProvider, setGameProvider] = useState(null);
	const [low6GameType, setLow6GameType] = useState(0);

	useEffect(() => {
		if (open && gameProviderId) {
			setLoading(true);
			setError(null);
			setSuccess(false);

			get(`/admin/arcade/${gameProviderId}/game-settings`)
				.then((settingsResponse) => {
					setGameProvider(settingsResponse.gameProvider);
					setLow6GameType(settingsResponse.low6GameType || 0);

					// Set game settings
					if (settingsResponse.gameSettings) {
						try {
							const settings = settingsResponse.gameSettings;
							setGameConfig(settings);
						} catch (err) {
							console.error("Error parsing game settings:", err);
							setGameConfig(defaultConfig);
						}
					} else {
						setGameConfig(defaultConfig);
					}
					setLoading(false);
				})
				.catch((err) => {
					console.error("Error loading game provider data:", err);
					setError("Failed to load game provider data");
					setLoading(false);
				});
		}
	}, [open, gameProviderId]);

	const handleSave = () => {
		setSaving(true);
		setError(null);
		setSuccess(false);
		put(`/admin/arcade/${gameProviderId}/game-settings`, {
			gameSettings: gameConfig,
		})
			.then(() => {
				setSuccess(true);
				setSaving(false);
				setTimeout(() => {
					onSuccessHandler();
				}, 500);
			})
			.catch((err) => {
				console.error("Error saving game settings:", err);
				setError(err.response?.data?.message || "Failed to save game settings");
				setSaving(false);
			});
	};

	const handleClose = () => {
		if (!saving) {
			setGameConfig(defaultConfig);
			setError(null);
			setSuccess(false);
			onClose();
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
			<DialogTitle>Game Configuration</DialogTitle>
			<DialogContent>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress />
					</Box>
				) : (
					<Box sx={{ mt: 1 }}>
						{gameProvider && (
							<Typography variant="h6" gutterBottom>
								{gameProvider.name} Configuration
							</Typography>
						)}
						{getGameConfigComponent(low6GameType, {
							gameConfig,
							setGameConfig,
							loading,
						})}
						{error && (
							<Alert severity="error" sx={{ mt: 2 }}>
								{error}
							</Alert>
						)}
						{success && (
							<Alert severity="success" sx={{ mt: 2 }}>
								Game settings saved successfully!
							</Alert>
						)}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={saving}>
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained" disabled={loading || saving}>
					{saving ? <CircularProgress size={20} /> : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
